import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText, FormGroup } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

// DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const CreatePurchasePayment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [purchases, setPurchases] = useState([]);
    const [selectedPurchaseData, setSelectedPurchaseData] = useState(null);
    const [formData, setFormData] = useState({
        purchase_id: "",
        amount: "",
        payment_method: "cash",
        note: "",
        created_at: dayjs().format('YYYY-MM-DD')
    });

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#2e4a3d' : '#dee2e6',
            boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(46, 74, 61, 0.25)' : 'none',
        })
    };

    useEffect(() => {
        const loadPurchases = async () => {
            dispatch(loadingTrue());
            try {
                const res = await Handler({ method: "GET", url: "/purchase/list.php?limit=0" });
                if (res?.success) {
                    // 🔥 Safely handle variations in the list API response without crashing
                    const options = res.data
                        .filter(item => {
                            const status = item.payment_summary?.status || item.status;
                            return status !== 'paid' && status !== 'completed';
                        })
                        .map(item => {
                            const pId = item.id || item.purchase?.id || item.purchase_id;
                            const fName = item.farmer?.name || item.farmer_name || "Unknown";
                            const bal = item.payment_summary?.balance ?? item.payment_summary?.balance_amount ?? 0;
                            return {
                                value: pId,
                                label: `ID: #${pId} | ${fName.toUpperCase()} | Balance: ₹${parseFloat(bal).toLocaleString('en-IN')}`,
                            };
                        });
                    setPurchases(options);
                }
            } catch (err) {
                toast.error("Failed to load purchase list");
            } finally {
                dispatch(loadingFalse());
            }
        };
        loadPurchases();
    }, [dispatch]);

    const handlePurchaseChange = async (opt) => {
        if (!opt) {
            setSelectedPurchaseData(null);
            setFormData({ ...formData, purchase_id: "", amount: "" });
            return;
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: `/purchase/view.php?id=${opt.value}` });
            if (res?.success) {
                const data = res.data;
                setSelectedPurchaseData(data);
                
                // 🔥 Fetch exact balance from view.php response robustly
                const currentBalance = data.payment_summary?.balance ?? data.payment_summary?.balance_amount ?? 0;
                
                setFormData({ 
                    ...formData, 
                    purchase_id: opt.value, 
                    amount: currentBalance 
                });
            }
        } catch (err) {
            toast.error("Error fetching purchase details");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.purchase_id) return toast.warning("Please select a purchase");
        if (!formData.amount || formData.amount <= 0) return toast.warning("Enter a valid amount");

        // 🔥 Validate against the exact balance securely
        const balance = parseFloat(selectedPurchaseData?.payment_summary?.balance ?? selectedPurchaseData?.payment_summary?.balance_amount ?? 0);
        if (parseFloat(formData.amount) > balance) {
            return toast.error(`Amount exceeds remaining balance (Max: ₹${balance.toLocaleString('en-IN')})`);
        }

        dispatch(loadingTrue());
        try {
            // 🔥 Properly hits your new PHP API and passes exact payload
            const res = await Handler({
                method: "POST",
                url: "/purchase_payments/create.php", 
                data: {
                    purchase_id: parseInt(formData.purchase_id),
                    amount: parseFloat(formData.amount),
                    payment_method: formData.payment_method,
                    note: formData.note,
                    created_at: formData.created_at
                }
            });

            if (res?.success) {
                Swal.fire("Success!", "Payment recorded successfully", "success");
                navigate("/purchases");
            } else {
                toast.error(res?.message || "Payment failed");
            }
        } catch (err) {
            Swal.fire("Error", "Server connection failed", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 p-md-5 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold text-theme m-0">Create Payment</h3>
                    <p className="text-muted small m-0">Record and track vendor payments</p>
                </div>
                <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                    <i className="bx bx-arrow-back me-1"></i> Back
                </Button>
            </div>

            <Row className="justify-content-center">
                <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <CardBody className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <FormGroup className="mb-4">
                                    <Label className="small fw-bold text-uppercase opacity-75">Select Purchase Record</Label>
                                    <Select
                                        options={purchases}
                                        styles={customSelectStyles}
                                        onChange={handlePurchaseChange}
                                        placeholder="Search by Farmer or ID..."
                                        isClearable
                                    />
                                </FormGroup>

                                {selectedPurchaseData && (
                                    <div className="mb-4 p-3 bg-light rounded-3 border-start border-4 border-success">
                                        <Row className="g-2">
                                            <Col xs={6}>
                                                <small className="text-muted d-block">Farmer / Trader</small>
                                                <span className="fw-bold">{(selectedPurchaseData.farmer?.name || "Unknown").toUpperCase()}</span>
                                            </Col>
                                            <Col xs={6} className="text-end">
                                                <small className="text-muted d-block">Current Balance</small>
                                                <span className="fw-bold text-danger">₹{parseFloat(selectedPurchaseData.payment_summary?.balance ?? selectedPurchaseData.payment_summary?.balance_amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                <FormGroup className="d-flex flex-column mb-4">
                                    <Label className="small fw-bold text-uppercase opacity-75">Payment Date *</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={dayjs(formData.created_at)}
                                        onChange={(newValue) => {
                                            setFormData({ ...formData, created_at: newValue ? newValue.format('YYYY-MM-DD') : '' });
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: 'white',
                                                        borderRadius: '12px',
                                                        height: '50px',
                                                        '& fieldset': { borderColor: '#dee2e6' },
                                                        '&:hover fieldset': { borderColor: '#2e4a3d' },
                                                    },
                                                    '& .MuiInputBase-input': { fontWeight: 'bold' }
                                                }
                                            }
                                        }}
                                    />
                                </FormGroup>

                                <FormGroup className="mb-4">
                                    <Label className="small fw-bold text-uppercase opacity-75">Amount to Pay</Label>
                                    <InputGroup size="lg">
                                        <InputGroupText className="bg-white border-end-0 text-success fw-bold">₹</InputGroupText>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            className="border-start-0 ps-0 fw-bold"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            placeholder="0.00"
                                            required
                                        />
                                    </InputGroup>
                                </FormGroup>

                                <FormGroup className="mb-4">
                                    <Label className="small fw-bold text-uppercase opacity-75">Payment Mode</Label>
                                    <div className="d-flex gap-2">
                                        {['cash', 'upi', 'bank'].map((method) => (
                                            <Button
                                                key={method}
                                                type="button"
                                                outline={formData.payment_method !== method}
                                                color={formData.payment_method === method ? "success" : "secondary"}
                                                className="flex-fill text-capitalize rounded-3"
                                                onClick={() => setFormData({ ...formData, payment_method: method })}
                                            >
                                                {method}
                                            </Button>
                                        ))}
                                    </div>
                                </FormGroup>

                                <FormGroup className="mb-4">
                                    <Label className="small fw-bold text-uppercase opacity-75">Note / Remarks</Label>
                                    <Input
                                        type="textarea"
                                        rows="2"
                                        className="rounded-3"
                                        placeholder="Optional payment reference..."
                                        value={formData.note}
                                        onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    />
                                </FormGroup>

                                <Button color="success" block className="py-3 fw-bold rounded-4 shadow w-100" type="submit">
                                    <i className="bx bx-check-circle me-1"></i> SAVE PAYMENT
                                </Button>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
        </LocalizationProvider>
    );
};

export default CreatePurchasePayment;